import { useState, useMemo, ReactNode } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog";
import { X, ExternalLink, Download } from "lucide-react";

interface ImageLightboxProps {
    /** The image URL to display in the lightbox */
    src: string;
    /** Alt text for the image */
    alt?: string;
    /** Optional title for the dialog header */
    title?: string;
    /** Whether to show download button (default: true) */
    showDownload?: boolean;
    /** Whether to show open in new tab button (default: true) */
    showOpenInNewTab?: boolean;
    /** Custom filename for download */
    downloadFilename?: string;
    /** Render prop for the trigger element */
    children: (props: { onClick: () => void }) => ReactNode;
}

export function ImageLightbox({
    src,
    alt = "Image",
    title,
    showDownload = true,
    showOpenInNewTab = true,
    downloadFilename,
    children,
}: ImageLightboxProps) {
    const [open, setOpen] = useState(false);

    const filename = useMemo(() => {
        if (downloadFilename) return downloadFilename;
        try {
            const u = new URL(src);
            return decodeURIComponent(u.pathname.split("/").pop() || "image.jpg");
        } catch {
            return "image.jpg";
        }
    }, [src, downloadFilename]);

    return (
        <>
            {children({ onClick: () => setOpen(true) })}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-5xl p-0 bg-black flex flex-col items-center">
                    <DialogHeader className="flex items-center justify-between px-3 py-2 w-full">
                        <div className="flex flex-col">
                            <DialogTitle className="text-sm text-white/90">
                                {title || alt}
                            </DialogTitle>
                            <DialogDescription className="text-xs text-white/60">
                                Click image to toggle fit
                            </DialogDescription>
                        </div>

                        <div className="flex items-center gap-2">
                            {showOpenInNewTab && (
                                <a
                                    href={src}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs bg-white/10 hover:bg-white/15 text-white transition"
                                    title="Open in new tab"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Open
                                </a>
                            )}
                            {showDownload && (
                                <a
                                    href={src}
                                    download={filename}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs bg-white/10 hover:bg-white/15 text-white transition"
                                    title="Download"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </a>
                            )}
                            <DialogClose
                                className="ml-1 inline-flex items-center justify-center rounded-md p-2 hover:bg-white/10 text-white transition"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </DialogClose>
                        </div>
                    </DialogHeader>

                    <LightboxImage src={src} alt={alt} />
                </DialogContent>
            </Dialog>
        </>
    );
}

/** Image element with click-to-toggle fit/zoom behavior inside the dialog */
function LightboxImage({ src, alt }: { src: string; alt?: string }) {
    const [contain, setContain] = useState(true);
    return (
        <div className="relative max-h-[80vh] w-full flex items-center justify-center">
            <img
                src={src}
                alt={alt}
                onClick={() => setContain((v) => !v)}
                className={`select-none cursor-zoom-in ${contain
                        ? "max-h-[80vh] w-auto object-contain"
                        : "max-h-[80vh] w-full object-cover"
                    }`}
            />
        </div>
    );
}
